import React, { useState, useEffect, useCallback } from 'react';
import { Clock, UserPlus, Pencil, Trash2, Pill, ClipboardList, Stethoscope, StickyNote, GitMerge, ChevronDown, ChevronRight } from 'lucide-react';
import ApiService from './ApiService';

// Action metadata: label, icon, tone
const ACTION_META = {
  PATIENT_CREATED:     { label: 'Patient created',        Icon: UserPlus,      tone: 'text-pos' },
  PATIENT_UPDATED:     { label: 'Patient info edited',    Icon: Pencil,        tone: 'text-spruce-600' },
  PATIENT_DELETED:     { label: 'Patient deleted',        Icon: Trash2,        tone: 'text-danger' },
  MEDICATION_ASSIGNED: { label: 'Medication assigned',    Icon: Pill,          tone: 'text-pos' },
  MEDICATION_REMOVED:  { label: 'Medication removed',     Icon: Pill,          tone: 'text-danger' },
  PROTOCOL_ASSIGNED:   { label: 'Protocol assigned',      Icon: ClipboardList, tone: 'text-pos' },
  PROTOCOL_UPDATED:    { label: 'Protocol updated',       Icon: ClipboardList, tone: 'text-spruce-600' },
  PROTOCOL_REMOVED:    { label: 'Protocol removed',       Icon: ClipboardList, tone: 'text-danger' },
  SERVICE_ASSIGNED:    { label: 'Service scheduled',      Icon: Stethoscope,   tone: 'text-pos' },
  SERVICE_REMOVED:     { label: 'Service removed',        Icon: Stethoscope,   tone: 'text-danger' },
  NOTE_CREATED:        { label: 'Note added',             Icon: StickyNote,    tone: 'text-pos' },
  NOTE_UPDATED:        { label: 'Note edited',            Icon: StickyNote,    tone: 'text-spruce-600' },
  NOTE_DELETED:        { label: 'Note deleted',           Icon: StickyNote,    tone: 'text-danger' },
  PATIENTS_MERGED:     { label: 'Duplicate records merged', Icon: GitMerge,    tone: 'text-copper-600' },
  DUPLICATE_DISMISSED: { label: 'Marked not a duplicate', Icon: GitMerge,      tone: 'text-ink-soft' },
  BULK_IMPORT_STARTED: { label: 'Bulk import',            Icon: UserPlus,      tone: 'text-spruce-600' },
};

// One-line human summary of an entry's details
function summarize(entry) {
  const d = entry.details || {};
  switch (entry.action) {
    case 'PATIENT_UPDATED': {
      const fields = Object.keys(d.changed || {});
      return fields.length ? `Changed: ${fields.join(', ')}` : null;
    }
    case 'MEDICATION_ASSIGNED':
      return [d.medicationName, d.dosage, d.frequency].filter(Boolean).join(' · ');
    case 'MEDICATION_REMOVED':
      return d.snapshot?.medicationName || null;
    case 'PROTOCOL_ASSIGNED':
      return [d.protocolName, d.endDate ? `through ${d.endDate}` : 'open-ended'].filter(Boolean).join(' · ');
    case 'PROTOCOL_REMOVED':
      return d.snapshot?.protocolName || null;
    case 'SERVICE_ASSIGNED':
      return [d.serviceName, d.nextDueDate ? `due ${d.nextDueDate}` : null].filter(Boolean).join(' · ');
    case 'SERVICE_REMOVED':
      return d.snapshot?.serviceName || null;
    case 'NOTE_CREATED':
      return d.preview ? `"${d.preview}${d.preview.length >= 120 ? '…' : ''}"` : null;
    case 'PATIENTS_MERGED':
      return `${(d.mergedPatients || []).length} record(s) merged into primary · ${d.recordsTransferred ?? 0} items transferred`;
    default:
      return null;
  }
}

const PatientHistorySection = ({ patientId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getPatientAudit(patientId);
      setEntries(data.entries || []);
      setError('');
    } catch (err) {
      setError('Could not load history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading history...</div>;
  if (error) return <div className="text-danger text-sm py-4">{error}</div>;

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock className="mx-auto mb-3 opacity-40" size={32} />
        <p>No recorded activity for this patient yet.</p>
        <p className="text-xs mt-1 text-gray-400">Actions taken from now on will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Action History</h3>
        <span className="text-xs font-data text-ink-soft">{entries.length} EVENTS</span>
      </div>

      <ol className="relative border-l border-line ml-3">
        {entries.map((entry) => {
          const meta = ACTION_META[entry.action] || { label: entry.action, Icon: Clock, tone: 'text-ink-soft' };
          const { Icon } = meta;
          const summary = summarize(entry);
          const isOpen = expanded === entry.auditId;
          const when = new Date(entry.timestamp);

          return (
            <li key={entry.auditId} className="mb-5 ml-5">
              <span className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full bg-white border border-line ${meta.tone}`}>
                <Icon size={13} />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className={`font-medium text-sm ${meta.tone}`}>{meta.label}</span>
                <span className="text-xs text-ink-soft">by {entry.performedByEmail || entry.performedBy}</span>
                <span className="text-xs font-data text-gray-400">
                  {when.toLocaleDateString()} {when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {summary && <div className="text-sm text-gray-600 mt-0.5">{summary}</div>}

              {entry.details && Object.keys(entry.details).length > 0 && (
                <button
                  onClick={() => setExpanded(isOpen ? null : entry.auditId)}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-spruce-600 hover:text-spruce-800"
                >
                  {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {isOpen ? 'Hide details' : 'View details'}
                </button>
              )}
              {isOpen && (
                <pre className="mt-2 p-3 bg-mist border border-line rounded text-[11px] font-data overflow-x-auto max-h-64">
{JSON.stringify(entry.details, null, 2)}
                </pre>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default PatientHistorySection;
